import NavBarContent from "@/domain/entities/navbar/NavBarContent";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import navBarContentMapper from "@/mappers/navbar/navBarContentMapper";
import {
    NavBarContent as NavBarContentSDK,
    useSdk,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useGetEventBus from "./useGetEventBus";
import { useToast } from "./utils/useToast";

const useGetNavbarData = (): {
    navbarData: NavBarContent | undefined;
    unProcessedFocusMailCount: number | undefined;
    isLoading: boolean;
} => {
    const { mailManager } = useSdk();
    const { toast } = useToast();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const fetchNavbarData = async (): Promise<NavBarContent | undefined> => {
        try {
            const navBarData = await mailManager.getNavBarContent();
            if (!navBarData) {
                throw new Error("Navbar data is undefined");
            }

            return navBarContentMapper(navBarData);
        } catch (error) {
            toast({
                title: t("errors.navbar"),
                description: t("errors.global.description"),
                variant: "error",
            });

            console.error(error);

            return undefined;
        }
    };

    const { data: navbarData, isLoading } = useQuery<
        NavBarContent | undefined,
        Error
    >({
        queryKey: queryKeys.navbarData(),
        queryFn: fetchNavbarData,
    });

    const unProcessedFocusMailCount =
        navbarData?.unProcessedFocusMailCount || 0;

    const handleNavigationUpdated = async (newNavBarData: NavBarContentSDK) => {
        await queryClient.cancelQueries({ queryKey: queryKeys.navbarData() });
        queryClient.setQueryData(
            queryKeys.navbarData(),
            navBarContentMapper(newNavBarData),
        );
    };

    const { off: offNavigationU } = useGetEventBus<NavBarContentSDK>(
        eventKeys.navigation.updated,
        handleNavigationUpdated,
    );

    useEffect(
        () => () =>
            offNavigationU(
                eventKeys.navigation.updated,
                handleNavigationUpdated,
            ),
        [offNavigationU],
    );

    return {
        navbarData,
        isLoading,
        unProcessedFocusMailCount,
    };
};

export default useGetNavbarData;
